// App state: one external store + a React hook. Tree edits go through commit() so
// undo/redo and autosave happen in one place.

import { useSyncExternalStore } from 'react';
import { migrate } from '../io/native';
import * as db from '../model/db';
import type { FileBag } from '../model/files';
import type { CiteTarget } from '../model/research';
import type { PersonField } from '../model/suggestions';
import { stampEdits, stampSettings, syncedSettings, type SyncedSettings, type TreesPlan } from '../model/sync';
import type { Id, MediaItem, Settings, Tree } from '../model/types';
import type { Parsed } from './components/ImportExport';
import { DEFAULT_THEME_ID } from './themes';

export type View = 'tree' | 'network' | 'people' | 'relate' | 'groups' | 'reports' | 'countdowns' | 'map' | 'media' | 'sources' | 'todos';

/** The sidebar's sections, each a set of views shown as tabs across the top of the page. */
export type Section = 'tree' | 'people' | 'explore' | 'research';
export const SECTIONS: { id: Section; label: string; views: View[] }[] = [
  { id: 'tree', label: 'Tree', views: ['tree', 'network'] },
  { id: 'people', label: 'People', views: ['people', 'groups'] },
  { id: 'explore', label: 'Explore', views: ['relate', 'reports', 'countdowns', 'map'] },
  { id: 'research', label: 'Research', views: ['media', 'sources', 'todos'] },
];

export function sectionOf(view: View): Section {
  return SECTIONS.find((s) => s.views.includes(view))!.id;
}

export interface Toast {
  id: number;
  text: string;
  kind: 'info' | 'error' | 'success';
  action?: { label: string; run: () => void };
}

export type Dialog =
  /** `field` opens the editor at one field (a birth date, a photo) instead of the name. */
  | { type: 'edit-person'; personId: Id; field?: PersonField }
  | { type: 'person-details'; personId: Id }
  /** `existing` starts on picking someone already in the tree rather than adding someone new. */
  | { type: 'add-relative'; personId: Id; kind: 'parent' | 'child' | 'partner' | 'sibling' | 'social'; existing?: boolean }
  | { type: 'edit-relationship'; relId: Id }
  | { type: 'new-person' }
  /** 'merge' adds the file's people to the open tree; 'new' opens it as a tree of its own. */
  | { type: 'import'; initial?: Parsed; mode: 'merge' | 'new' }
  /** `picture` starts on a chart format (from the Tree view). */
  | { type: 'export'; picture?: boolean }
  | { type: 'trees' }
  | { type: 'settings' }
  | { type: 'sync' }
  | { type: 'help' }
  | { type: 'palette' }
  | { type: 'edit-group'; groupId: Id | null }
  | { type: 'group-details'; groupId: Id }
  /** `otherId` starts the dialog with the duplicate already chosen. */
  | { type: 'merge'; personId: Id; otherId?: Id }
  | { type: 'bulk-edit'; personIds: Id[] }
  /** `draft` is a new item that isn't in the tree until it's saved. */
  | { type: 'media-details'; mediaId: Id; draft?: MediaItem }
  /** A new source (null) or an existing one; `onSave` hears the saved source's id. */
  | { type: 'edit-source'; sourceId: Id | null; onSave?: (id: Id) => void }
  | { type: 'edit-todo'; todoId: Id | null; personIds?: Id[] }
  /** Add a citation for a person (no `index`), or edit the one at `target`/`index`. */
  | { type: 'edit-citation'; personId: Id; target?: CiteTarget; index?: number; sourceId?: Id }
  | { type: 'confirm'; title: string; body: string; confirmLabel: string; danger?: boolean; onConfirm: () => void };

export interface AppState {
  ready: boolean;
  trees: db.TreeMeta[];
  tree: Tree | null;
  past: Tree[];
  future: Tree[];
  view: View;
  focusId: Id | null;
  selectedId: Id | null;
  relateA: Id | null;
  relateB: Id | null;
  peopleQuery: string;
  settings: Settings;
  saveState: 'saved' | 'saving' | 'error' | 'memory';
  /** Whether the browser has promised to keep the app's storage (see db.persistence). */
  persistence: db.Persistence;
  dialogs: Dialog[];
  toasts: Toast[];
}

const SETTINGS_KEY = 'relationships.settings';
const LAST_TREE_KEY = 'relationships.lastTree';
const HISTORY_LIMIT = 100;

function loadSettings(): Settings {
  const defaults: Settings = { theme: 'system', themeId: DEFAULT_THEME_ID };
  try {
    const saved: Settings = { ...defaults, ...JSON.parse(db.lsGet(SETTINGS_KEY) ?? '{}') };
    // Times from the version that synced only kinds of suggestion move into settingsAt.
    if (!saved.suggestionKindsAt) return saved;
    const { suggestionKindsAt: _, ...rest } = saved;
    return { ...rest, settingsAt: syncedSettings(saved).settingsAt };
  } catch {
    return defaults;
  }
}

let state: AppState = {
  ready: false,
  trees: [],
  tree: null,
  past: [],
  future: [],
  view: 'tree',
  focusId: null,
  selectedId: null,
  relateA: null,
  relateB: null,
  peopleQuery: '',
  settings: loadSettings(),
  saveState: 'saved',
  persistence: 'unsupported',
  dialogs: [],
  toasts: [],
};

const listeners = new Set<() => void>();

export function getState(): AppState {
  return state;
}

export function setState(patch: Partial<AppState> | ((s: AppState) => Partial<AppState>)) {
  const p = typeof patch === 'function' ? patch(state) : patch;
  state = { ...state, ...p };
  for (const l of listeners) l();
}

export function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Subscribe to a slice. The selector must return a stable value (state field or primitive). */
export function useApp<T>(selector: (s: AppState) => T): T {
  return useSyncExternalStore(subscribe, () => selector(state), () => selector(state));
}

// ---------------------------------------------------------------- persistence

let saveTimer: ReturnType<typeof setTimeout> | undefined;

// Every save writes the whole tree, so a second tab still showing an older copy would erase this
// tab's work the next time it saved. Tabs say when they save or delete a tree, and a tab showing
// that tree loads the newer copy.
type TabNote = { type: 'saved' | 'deleted'; treeId: string; updatedAt?: string } | { type: 'hello' | 'here' };
const tabs = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('relationships-trees') : null;
const tellTabs = (note: TabNote) => tabs?.postMessage(note);
let otherTabSeen = false;
if (tabs)
  tabs.onmessage = (e: MessageEvent<TabNote>) => {
    const note = e.data;
    if (note.type === 'hello') tellTabs({ type: 'here' });
    else if (note.type === 'here') otherTabSeen = true;
    else if ('treeId' in note) void heardFromTab(note);
  };

/** Whether the app is open in another tab, which may still need files no saved tree lists. */
async function aloneInBrowser(): Promise<boolean> {
  if (!tabs) return true;
  otherTabSeen = false;
  tellTabs({ type: 'hello' });
  await new Promise((r) => setTimeout(r, 300));
  return !otherTabSeen;
}

async function heardFromTab(note: Extract<TabNote, { treeId: string }>) {
  const trees = await db.listTrees();
  const open = state.tree;
  if (!open || open.id !== note.treeId) return setState({ trees });
  if (note.type === 'deleted') {
    clearTimeout(saveTimer);
    toast(`“${open.name}” was deleted in another tab.`, 'info');
    await afterOpenTreeDeleted(trees);
    return;
  }
  // Unsaved changes here are about to be saved, and the other tab will load them in turn.
  if (open.updatedAt === note.updatedAt || state.saveState === 'saving') return setState({ trees });
  const t = await db.loadTree(note.treeId);
  if (!t || state.tree !== open) return setState({ trees });
  migrate(t);
  // Undo here would bring back the older copy and quietly undo the other tab's work, so history starts over.
  setState({ trees, tree: t, past: [], future: [], saveState: 'saved', ...peopleRefsFor(t) });
  pruneDialogsSoon();
  toast('Updated with changes made in another tab.', 'info');
}

function scheduleSave() {
  if (db.isMemoryOnly()) {
    setState({ saveState: 'memory' });
    return;
  }
  setState({ saveState: 'saving' });
  clearTimeout(saveTimer);
  saveTimer = setTimeout(flushSave, 400);
}

export async function flushSave() {
  clearTimeout(saveTimer);
  const t = state.tree;
  if (!t || state.saveState === 'saved') return;
  try {
    await db.saveTree(t);
    tellTabs({ type: 'saved', treeId: t.id, updatedAt: t.updatedAt });
    dropRecovery(t);
    const trees = await db.listTrees();
    // Only mark saved if nothing changed while we were writing.
    setState((s) => ({ trees, saveState: s.tree === t ? 'saved' : s.saveState }));
  } catch (e) {
    console.error(e);
    setState({ saveState: 'error' });
    toast(`Couldn't save: ${(e as Error).message}. Export a copy so nothing is lost.`, 'error', { label: 'Export', run: () => openDialog({ type: 'export' }) });
  }
}

// A save started as the page closes never finishes: the browser aborts IndexedDB writes still
// running when a page unloads. So as the page hides with changes not yet saved, the tree is also
// written to localStorage, which is synchronous, and the next start saves that copy properly.
const RECOVERY_PREFIX = 'relationships.unsaved.';

/** Keep the open tree's unsaved changes somewhere a closing page can still write. */
export function keepUnsaved() {
  const t = state.tree;
  if (!t || (state.saveState !== 'saving' && state.saveState !== 'error')) return;
  // Too big for localStorage (a few MB) fails quietly; the regular save is still on its way.
  db.lsSet(RECOVERY_PREFIX + t.id, JSON.stringify(t));
}

/** Once `t` is saved, a recovery copy no newer than it isn't needed. */
function dropRecovery(t: Tree) {
  const raw = db.lsGet(RECOVERY_PREFIX + t.id);
  if (raw === null) return;
  try {
    if ((JSON.parse(raw) as Tree).updatedAt > t.updatedAt) return;
  } catch {
    /* unreadable; drop it */
  }
  db.lsRemove(RECOVERY_PREFIX + t.id);
}

/** Save any recovery copies newer than the saved trees they belong to, then drop them. */
async function recoverUnsaved() {
  for (const key of db.lsKeys().filter((k) => k.startsWith(RECOVERY_PREFIX))) {
    try {
      const kept = JSON.parse(db.lsGet(key) ?? '') as Tree;
      const saved = await db.loadTree(kept.id);
      // A deleted tree stays deleted, and another tab may have saved something newer since.
      if (saved && kept.updatedAt > saved.updatedAt) {
        await db.saveTree(kept);
        tellTabs({ type: 'saved', treeId: kept.id, updatedAt: kept.updatedAt });
      }
    } catch (e) {
      console.error(e);
    }
    db.lsRemove(key);
  }
}

export async function init() {
  const ok = await db.storageAvailable();
  if (ok) await recoverUnsaved();
  if (ok) await moveDismissedIntoTrees();
  const trees = ok ? await db.listTrees() : [];
  const persistence = ok ? await db.persistence() : 'unsupported';
  const lastId = db.lsGet(LAST_TREE_KEY);
  const pick = trees.find((t) => t.id === lastId) ?? trees[0];
  let tree: Tree | null = null;
  if (pick) tree = (await db.loadTree(pick.id)) ?? null;
  setState({ ready: true, trees, persistence, saveState: ok ? 'saved' : 'memory' });
  // Media files no saved tree uses any more (deleted media, abandoned imports). Not while another
  // tab is open: it may have just added a file it hasn't saved yet, or need one for undo.
  if (ok)
    void aloneInBrowser().then((alone) => {
      if (alone) db.removeUnusedFiles().catch((e) => console.error(e));
    });
  // Restore the view and person from the URL (reload or shared link) before writing it back.
  if (tree) openLoadedTree(tree, false);
  applyHash();
  if (tree) syncHash(true);
  if (!ok) toast("Your browser isn't allowing storage, so changes will be lost when you close this tab. Export to save your work.", 'error');
}

/** keepDialogs: after a sync, whose dialog stays open to say what happened. */
function openLoadedTree(tree: Tree, updateUrl = true, keepDialogs = false) {
  migrate(tree);
  const focus = tree.homePersonId && tree.people[tree.homePersonId] ? tree.homePersonId : Object.keys(tree.people)[0] ?? null;
  db.lsSet(LAST_TREE_KEY, tree.id);
  setState((s) => ({ tree, past: [], future: [], focusId: focus, selectedId: null, relateA: focus, relateB: null, dialogs: keepDialogs ? s.dialogs : [], peopleQuery: '' }));
  if (updateUrl) syncHash();
}

export async function openTree(id: string) {
  await flushSave();
  const t = await db.loadTree(id);
  if (t) openLoadedTree(t);
}

/** Make a brand-new or imported tree the current one and save it, with any media files it brought. */
export async function adoptTree(tree: Tree, files?: FileBag) {
  await flushSave();
  if (files?.size) await db.putFiles(files);
  openLoadedTree(tree);
  await db.saveTree(tree);
  tellTabs({ type: 'saved', treeId: tree.id, updatedAt: tree.updatedAt });
  setState({ trees: await db.listTrees(), saveState: db.isMemoryOnly() ? 'memory' : 'saved' });
}

export async function removeTree(id: string) {
  // A save still waiting to run would write the open tree straight back after the delete.
  if (state.tree?.id === id) clearTimeout(saveTimer);
  await db.deleteTree(id);
  // So a sync deletes it on other devices too, rather than bringing it back.
  await db.setKv(DELETED_TREES_KEY, { ...(await deletedTrees()), [id]: new Date().toISOString() }).catch((e) => console.error(e));
  forgetBackup(id);
  db.lsRemove(RECOVERY_PREFIX + id);
  tellTabs({ type: 'deleted', treeId: id });
  // Its media files are cleaned up at the next start, not now: undo in the open tree may still
  // need a file that no saved tree lists.
  const trees = await db.listTrees();
  if (state.tree?.id === id) await afterOpenTreeDeleted(trees);
  setState({ trees });
}

/**
 * The open tree is gone: open the next one, or, with none left, show the welcome page on its own.
 * Dialogs go too (the trees list would otherwise sit over the welcome page, saying there are no
 * trees and repeating its buttons), and so does the address's view and person.
 */
async function afterOpenTreeDeleted(trees: db.TreeMeta[]) {
  setState({ trees, tree: null, past: [], future: [], focusId: null, selectedId: null, saveState: 'saved' });
  if (trees[0]) return openTree(trees[0].id);
  setState({ dialogs: [] });
  if (location.hash) history.replaceState(null, '', location.pathname + location.search);
}

// ---------------------------------------------------------------- device sync

const DELETED_TREES_KEY = 'deletedTrees';

/** Ids of trees deleted on this device, with when, so a sync deletes them elsewhere too. */
export async function deletedTrees(): Promise<Record<string, string>> {
  return (await db.getKv<Record<string, string>>(DELETED_TREES_KEY)) ?? {};
}

/** Every saved tree, with the open one's latest edits, for comparing with another device. */
export async function allTrees(): Promise<Tree[]> {
  await flushSave();
  const out: Tree[] = [];
  for (const m of await db.listTrees()) {
    const t = await db.loadTree(m.id);
    if (t) out.push(migrate(t));
  }
  return out;
}

/** Save what a sync with another device changes here (its media files must already be stored). */
export async function applySyncPlan(plan: TreesPlan) {
  await flushSave();
  clearTimeout(saveTimer);
  for (const t of plan.put) {
    await db.saveTree(t);
    tellTabs({ type: 'saved', treeId: t.id, updatedAt: t.updatedAt });
  }
  for (const t of plan.del) {
    await db.deleteTree(t.id);
    forgetBackup(t.id);
    tellTabs({ type: 'deleted', treeId: t.id });
  }
  await db.setKv(DELETED_TREES_KEY, plan.deletedTrees);
  const trees = await db.listTrees();
  const open = state.tree;
  const openFirst = async () => {
    const t = await db.loadTree(trees[0].id);
    setState({ trees, saveState: 'saved' });
    if (t) openLoadedTree(t, true, true);
  };
  if (open && plan.del.some((t) => t.id === open.id)) {
    if (trees[0]) return openFirst();
    // Unlike afterOpenTreeDeleted, the Sync dialog stays open to finish and say what happened.
    setState({ trees, tree: null, past: [], future: [], focusId: null, selectedId: null, saveState: 'saved' });
    if (location.hash) history.replaceState(null, '', location.pathname + location.search);
    return;
  }
  const changed = open && plan.put.find((t) => t.id === open.id);
  if (changed) {
    migrate(changed);
    // Undo here would bring back the older copy, so history starts over (as for another tab's save).
    setState({ trees, tree: changed, past: [], future: [], saveState: 'saved', ...peopleRefsFor(changed) });
    pruneDialogsSoon();
  } else setState({ trees });
  // A new device that had no trees opens the first one it was sent.
  if (!open && trees[0]) await openFirst();
}

// ---------------------------------------------------------------- keeping storage

// Asked on the first edit, not on load: Firefox shows a prompt for it, which should follow
// something the user did. Chrome and Safari decide without asking.
let askedPersistence = false;
function askPersistenceOnce() {
  if (askedPersistence || state.persistence !== 'not-granted') return;
  askedPersistence = true;
  void requestPersistence();
}

export async function requestPersistence(): Promise<db.Persistence> {
  const persistence = await db.requestPersistence();
  setState({ persistence });
  return persistence;
}

// When each tree was last saved to a complete Relationships file. Kept in localStorage, not the
// tree's meta, which every save rebuilds.
const BACKUPS_KEY = 'relationships.backups';
export interface Backup {
  /** When the backup was made (ISO). */
  at: string;
  /** The tree's updatedAt then, to tell whether it has changed since. */
  updatedAt: string;
}

function backups(): Record<string, Backup> {
  try {
    return JSON.parse(db.lsGet(BACKUPS_KEY) ?? '{}');
  } catch {
    return {};
  }
}

export function lastBackup(treeId: string): Backup | undefined {
  return backups()[treeId];
}

/** Note that `tree` was just saved, as it is, to a Relationships file. */
export function markBackedUp(tree: Tree) {
  db.lsSet(BACKUPS_KEY, JSON.stringify({ ...backups(), [tree.id]: { at: new Date().toISOString(), updatedAt: tree.updatedAt } }));
}

function forgetBackup(treeId: string) {
  const { [treeId]: _, ...rest } = backups();
  db.lsSet(BACKUPS_KEY, JSON.stringify(rest));
}

// Suggestions dismissed in a tree used to be kept on the device, in localStorage. They're in the
// tree now (tree.dismissed), so they sync and are backed up; the old ones move in at startup.
const DISMISSED_KEY = 'relationships.dismissedSuggestions';

async function moveDismissedIntoTrees() {
  const raw = db.lsGet(DISMISSED_KEY);
  if (raw === null) return;
  let byTree: unknown = null;
  try {
    byTree = JSON.parse(raw);
  } catch {
    /* unreadable; dropped below */
  }
  try {
    for (const [id, ids] of Object.entries(byTree && typeof byTree === 'object' ? byTree : {})) {
      const t = Array.isArray(ids) ? await db.loadTree(id) : undefined;
      if (!t) continue;
      // Untimed, like everything from before sync: a dismissal undone on another device since wins.
      const dismissed = { ...t.dismissed };
      for (const x of ids as unknown[]) if (typeof x === 'string') dismissed[x] = true;
      await db.saveTree({ ...t, dismissed });
    }
  } catch (e) {
    // A save failed: try again at the next start.
    console.error(e);
    return;
  }
  db.lsRemove(DISMISSED_KEY);
}

// ---------------------------------------------------------------- edits & history

export function commit(next: Tree) {
  const cur = state.tree;
  if (!cur || next === cur) return;
  next = stampEdits(cur, next, new Date().toISOString());
  const past = [...state.past, cur];
  if (past.length > HISTORY_LIMIT) past.shift();
  setState({ tree: next, past, future: [], ...peopleRefsFor(next) });
  pruneDialogsSoon();
  scheduleSave();
  askPersistenceOnce();
}

/** Drop or replace references to people who aren't in the tree we're switching to. */
function peopleRefsFor(t: Tree): Pick<AppState, 'focusId' | 'selectedId' | 'relateA' | 'relateB'> {
  let { focusId, selectedId, relateA, relateB } = state;
  const firstId = Object.keys(t.people)[0] ?? null;
  if (focusId && !t.people[focusId]) focusId = t.homePersonId && t.people[t.homePersonId] ? t.homePersonId : firstId;
  if (!focusId) focusId = firstId;
  if (selectedId && !t.people[selectedId]) selectedId = null;
  if (relateA && !t.people[relateA]) relateA = null;
  if (relateB && !t.people[relateB]) relateB = null;
  return { focusId, selectedId, relateA, relateB };
}

function restore(t: Tree, past: Tree[], future: Tree[]) {
  // Undoing is an edit too, so a sync carries it to other devices.
  if (state.tree) t = stampEdits(state.tree, t, new Date().toISOString());
  setState({ tree: t, past, future, ...peopleRefsFor(t) });
  pruneDialogsSoon();
  scheduleSave();
}

/** Whether a dialog is about something the tree no longer has (deleted, undone, or changed in another tab). */
function orphaned(d: Dialog, t: Tree | null): boolean {
  if (!t) return false;
  switch (d.type) {
    case 'person-details':
    case 'edit-person':
    case 'add-relative':
    case 'merge':
    case 'edit-citation':
      return !t.people[d.personId];
    case 'edit-relationship':
      return !t.relationships[d.relId];
    case 'group-details':
      return !t.groups[d.groupId];
    case 'edit-group':
      return d.groupId !== null && !t.groups[d.groupId];
    case 'media-details':
      return !d.draft && !t.media[d.mediaId];
    case 'edit-source':
      return d.sourceId !== null && !t.sources[d.sourceId];
    case 'edit-todo':
      return d.todoId !== null && !t.todos[d.todoId];
    case 'bulk-edit':
      return !d.personIds.some((id) => t.people[id]);
    default:
      return false;
  }
}

let pruning = false;
/**
 * Close dialogs left showing someone or something that's gone, such as a person's details after
 * deleting them from their editor, which would otherwise leave an empty dimmed screen. Runs after
 * the current handler, so its own closeDialog() still closes the dialog it means to.
 */
function pruneDialogsSoon() {
  if (pruning) return;
  pruning = true;
  queueMicrotask(() => {
    pruning = false;
    if (state.dialogs.some((d) => orphaned(d, state.tree))) setState((s) => ({ dialogs: s.dialogs.filter((d) => !orphaned(d, s.tree)) }));
  });
}

export function undo() {
  const { past, tree, future } = state;
  if (!past.length || !tree) return;
  restore(past[past.length - 1], past.slice(0, -1), [tree, ...future]);
}

export function redo() {
  const { past, tree, future } = state;
  if (!future.length || !tree) return;
  restore(future[0], [...past, tree], future.slice(1));
}

// ---------------------------------------------------------------- navigation

/** Only the charts show the person sidebar; every other view closes it. */
export const PANEL_VIEWS: View[] = ['tree', 'network'];
export const hasPanel = (view: View) => PANEL_VIEWS.includes(view);

export function setView(view: View) {
  setState((s) => ({ view, selectedId: hasPanel(view) ? s.selectedId : null }));
  rememberTab(view);
  syncHash();
}

// Each section reopens the tab it was last on.
const TABS_KEY = 'relationships.sectionTabs';
function lastTabs(): Partial<Record<Section, View>> {
  try {
    return JSON.parse(db.lsGet(TABS_KEY) ?? '{}');
  } catch {
    return {};
  }
}
function rememberTab(view: View) {
  db.lsSet(TABS_KEY, JSON.stringify({ ...lastTabs(), [sectionOf(view)]: view }));
}

/** Open a section (on phones, where the sidebar shows sections) on the tab it was last on. */
export function openSection(id: Section) {
  const sec = SECTIONS.find((s) => s.id === id)!;
  if (sectionOf(state.view) === id) return;
  const last = lastTabs()[id];
  setView(last && sec.views.includes(last) ? last : sec.views[0]);
}

export function focusPerson(id: Id, opts: { select?: boolean; view?: View } = {}) {
  setState((s) => {
    const view = opts.view ?? s.view;
    return { focusId: id, selectedId: !hasPanel(view) ? null : opts.select === false ? s.selectedId : id, view };
  });
  syncHash();
}

/** Show someone's details: in place on a chart, otherwise by opening them in the tree. */
export function showPerson(id: Id) {
  if (hasPanel(state.view)) selectPerson(id);
  else openDialog({ type: 'person-details', personId: id });
}

export function selectPerson(id: Id | null) {
  setState({ selectedId: id });
}

export function updateSettings(patch: Partial<Settings>) {
  saveSettings(stampSettings(state.settings, { ...state.settings, ...patch }, new Date().toISOString()));
}

/** Take settings merged by a sync with another device, without stamping them as changed here. */
export function adoptSyncedSettings(synced: SyncedSettings) {
  saveSettings({ ...state.settings, ...synced });
}

function saveSettings(settings: Settings) {
  db.lsSet(SETTINGS_KEY, JSON.stringify(settings));
  setState({ settings });
}

// Another tab's settings change (or a sync there): take it, so a later change here doesn't write
// this tab's older copy back over it.
if (typeof window !== 'undefined')
  window.addEventListener('storage', (e) => {
    if (e.key === SETTINGS_KEY) setState({ settings: loadSettings() });
  });

const VIEWS: View[] = ['tree', 'network', 'people', 'relate', 'groups', 'reports', 'countdowns', 'map', 'media', 'sources', 'todos'];
/** Addresses of views that have since moved: Suggestions became part of To-dos. */
const VIEW_ALIASES: Record<string, View> = { suggestions: 'todos' };

function syncHash(replace = false) {
  const h = `#/${state.view}${state.focusId ? '/' + state.focusId : ''}`;
  if (location.hash === h) return;
  if (replace) history.replaceState(null, '', h);
  else history.pushState(null, '', h);
}

export function applyHash() {
  const m = location.hash.match(/^#\/(\w+)(?:\/([\w-]+))?/);
  if (!m) return;
  const view = VIEWS.includes(m[1] as View) ? (m[1] as View) : VIEW_ALIASES[m[1]] ?? state.view;
  const focus = m[2] && state.tree?.people[m[2]] ? m[2] : state.focusId;
  setState((s) => ({ view, focusId: focus, selectedId: hasPanel(view) ? s.selectedId : null }));
}

// ---------------------------------------------------------------- dialogs & toasts

export function openDialog(d: Dialog) {
  setState((s) => ({ dialogs: [...s.dialogs, d] }));
}

/** Swap the top dialog for another (e.g. the details dialog moving to a relative). */
export function replaceDialog(d: Dialog) {
  setState((s) => ({ dialogs: [...s.dialogs.slice(0, -1), d] }));
}

export function closeDialog() {
  setState((s) => ({ dialogs: s.dialogs.slice(0, -1) }));
}

let toastId = 0;
export function toast(text: string, kind: Toast['kind'] = 'info', action?: Toast['action']) {
  const id = ++toastId;
  setState((s) => ({ toasts: [...s.toasts, { id, text, kind, action }] }));
  setTimeout(() => dismissToast(id), kind === 'error' ? 9000 : 5000);
}

export function dismissToast(id: number) {
  setState((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
}

export function confirmAction(opts: Omit<Extract<Dialog, { type: 'confirm' }>, 'type'>) {
  openDialog({ type: 'confirm', ...opts });
}
